import React from 'react';

import { LOCATION_CHANGE } from 'connected-react-router';

import ActionTypes from './ActionTypes';

import type { AnyAction } from 'redux';

export interface Message {
	key: number;
	type: string;
	text: React.ReactNode;
	dismissable: boolean;
}

export interface State {
	messages: Message[];
	timeouts: Map<Message, NodeJS.Timeout>;
	counter: number;
}

function createState(): State {
	return {
		messages: [],
		timeouts: new Map(),
		counter: 0,
	};
}

export default function reducer(state: State = createState(), action: AnyAction): State {
	switch (action.type) {
		case ActionTypes.add: {
			const message = state.messages.find(
				(message) => action.message.type === message.type && action.message.text === message.text
			);

			return {
				...state,
				messages: message ? state.messages : [action.message, ...state.messages],
				timeouts: message ? state.timeouts : new Map(state.timeouts).set(action.message, action.timeout),
				counter: state.counter + 1,
			};
		}

		case ActionTypes.remove: {
			const timeouts = new Map();
			Array.from(state.timeouts.entries()).forEach(([message, timeout]) => {
				if (message === action.message) {
					clearTimeout(timeout);
				} else {
					timeouts.set(message, timeout);
				}
			});

			return {
				...state,
				messages: state.messages.filter((message) => message !== action.message),
				timeouts,
			};
		}

		case ActionTypes.removeByText: {
			const timeouts = new Map();
			Array.from(state.timeouts.entries()).forEach(([message, timeout]) => {
				if (timeout === action.timeout) {
					clearTimeout(timeout);
				} else {
					timeouts.set(message, timeout);
				}
			});

			return {
				...state,
				messages: state.messages.filter((message) => message.text !== action.text),
				timeouts,
			};
		}

		case ActionTypes.timeout: {
			const removed = new Set();
			const timeouts = new Map();

			Array.from(state.timeouts.entries()).forEach(([message, timeout]) => {
				if (timeout === action.timeout) {
					clearTimeout(timeout);
					removed.add(message);
				} else {
					timeouts.set(message, timeout);
				}
			});

			return {
				...state,
				timeouts,
				messages: state.messages.filter((message) => !removed.has(message)),
			};
		}

		case ActionTypes.reset:
		case LOCATION_CHANGE:
			if (state.messages.length === 0) {
				return state;
			}

			Array.from(state.timeouts.values()).forEach(clearTimeout);

			return {
				...state,
				messages: [],
				timeouts: new Map(),
			};

		case ActionTypes.cancelDismiss:
			Array.from(state.timeouts.values()).forEach((timeout) => {
				clearTimeout(timeout);
			});

			return {
				...state,
				timeouts: new Map(),
			};

		case ActionTypes.scheduleDismiss: {
			const timeouts = new Map(state.timeouts);

			state.messages.forEach((message) => {
				if (message.type !== 'error') {
					timeouts.set(message, action.timeout);
				}
			});

			return {
				...state,
				timeouts,
			};
		}

		default:
			return state;
	}
}
